import type {
  ClinicConversation,
  FaxConnection,
  FaxReceipt,
  FaxTransmission,
  OrderResultWorkflowStatus,
  OrderResultWorkQueueItem,
  PatientMessageAttentionList,
  StaffRequest,
} from "@emr/contracts";
import type { DocumentInbox } from "../api/incomingDocuments";
import type { ReferralWorkflowStatus, ReferralWorkQueueItem } from "../api/referrals";
import {
  PATIENT_MESSAGE_KIND_LABELS,
  patientMessageProblemLabel,
  patientMessageReasonText,
} from "../pages/schedule/patientMessageCopy";
import { documentProcessingFailureMessage } from "./documentProcessingFailure";
import { referralFaxFailureMessage } from "./faxStatus";

export type InboxBucket = "action" | "waiting" | "completed";
export type InboxTone = "neutral" | "blue" | "amber" | "red" | "green";
/** What kind of thing a row is; drawn as the tile at the left of the row. */
export type InboxType =
  | "lab"
  | "imaging"
  | "consult"
  | "referral"
  | "document"
  | "follow_up"
  | "upload"
  | "staff"
  | "message"
  | "patient_message";
// Conversations share the same type and bucket presentation as clinical work.
/** Timing metadata; the Attention page orders its flat list independently. */
export type InboxGroup = "now" | "today" | "week" | "later" | "yesterday" | "earlier";
/**
 * What the server will actually let this person do to the row from the list:
 * who a row can be handed to, how it can be put off, and how it can be closed.
 */
export type InboxCapabilities = {
  assign: "physician" | "staff" | null;
  snooze: "dates" | "contact" | "follow_up" | null;
  /**
   * acknowledge: "Mark done" on a patient text or email that needed a person (PA-7).
   * stop_tracking: an expected result that never arrived stops being tracked.
   * close_result: a received result ready for closure closes its loop.
   */
  done: "complete" | "discontinue" | "acknowledge" | "stop_tracking" | "close_result" | null;
};
export type InboxRow = {
  key: string;
  kind:
    | "report"
    | "obligation"
    | "follow_up"
    | "processing"
    | "result"
    | "referral"
    | "staff"
    | "message"
    | "fax"
    | "patient_message";
  id: string;
  /** The version a decision about this row must name; null when the inbox does not carry one. */
  version: number | null;
  patientId: string;
  patientName: string;
  /** The visit a staff request about this item is filed under, when one is known. */
  encounterId: string | null;
  type: InboxType;
  title: string;
  detail: string;
  status: string;
  /** A short state word for the row when the time fact alone would not explain it. */
  pill: string | null;
  tone: InboxTone;
  bucket: InboxBucket;
  group: InboxGroup;
  ownerId: string | null;
  coverageIds: string[];
  date: string | null;
  /** When the item entered its inbox; overdue work arrives when it becomes actionable. */
  arrivedAt?: string | null;
  dueAt?: string | null;
  completedAt?: string | null;
  unread?: boolean;
  dateLabel: string;
  /** A separate timing fact: "8d overdue", "Due today", "Thu", "Sep 24". */
  when: string;
  /** The one verb for this person, or null when the row only has somewhere to open. */
  action: string | null;
  /** Where the row goes when opened; the verb only says what you will do there. */
  opens:
    | "report"
    | "tracking"
    | "document"
    | "retry"
    | "result"
    | "referral"
    | "staff"
    | "message"
    | "fax"
    | "patient_message";
  can: InboxCapabilities;
  /** The report this row opens; for a result row, the result workflow id. */
  reportId: string | null;
  urgent: boolean;
  /** An abnormal report awaiting review, used by the optional priority ordering. */
  abnormal?: boolean;
  /** A patient text or email's booking (PA-7): what "Mark done" and its panel act on. */
  appointmentId?: string;
  /** A result row's investigation: what "Mark done" stops tracking when nothing arrived. */
  expectedEventId?: string;
};
export type Obligation = DocumentInbox["obligations"][number];

const investigationNames: Record<string, string> = {
  lab_result: "Laboratory results",
  imaging_report: "Imaging report",
  consult_report: "Consultation report",
};
export const investigationLabel = (type: string) => investigationNames[type] ?? "Investigation";
const investigationTypes: Record<string, InboxType> = {
  lab_result: "lab",
  imaging_report: "imaging",
  consult_report: "consult",
};
export const investigationType = (eventType: string): InboxType =>
  investigationTypes[eventType] ?? "document";
const reportTypes: Record<string, InboxType> = {
  lab: "lab",
  imaging: "imaging",
  consult: "consult",
};
export const groupLabels: Record<InboxGroup, string> = {
  now: "Now",
  today: "Today",
  week: "This week",
  later: "Later",
  yesterday: "Yesterday",
  earlier: "Earlier",
};
export const groupOrder: InboxGroup[] = ["now", "today", "week", "later", "yesterday", "earlier"];
/** Roles the server accepts as the staff owner who chases an investigation. */
export const chaseRoles = new Set([
  "front_desk",
  "nurse_ma",
  "referral_coordinator",
  "clinic_admin",
]);
const none: InboxCapabilities = { assign: null, snooze: null, done: null };
export function faxAttentionRows(
  receipts: FaxReceipt[],
  transmissions: FaxTransmission[],
  connection?: FaxConnection | null,
): InboxRow[] {
  const rows: InboxRow[] = [
    ...receipts.filter((item) =>
      ["matching_required", "routing_required", "quarantined", "failed"].includes(item.status),
    ),
    ...transmissions.filter((item) => ["failed", "uncertain"].includes(item.status)),
  ].map((item) => {
    const received = "receivedAt" in item;
    const urgent = received && item.urgent;
    const date = received ? item.receivedAt : item.updatedAt;
    return {
      key: `fax:${received ? "received" : "sent"}:${item.id}`,
      kind: "fax",
      id: item.id,
      version: item.version,
      patientId: received ? "" : (item.patientId ?? ""),
      patientName: received ? "Incoming fax" : "Outgoing fax",
      encounterId: null,
      type: "document",
      title: received ? `Fax from ${item.senderFax ?? "unknown number"}` : item.title,
      detail: item.failureCode
        ? referralFaxFailureMessage(item.failureCode)
        : item.status.replaceAll("_", " "),
      status: item.status,
      pill: item.status.replaceAll("_", " "),
      tone: urgent ? "red" : "amber",
      bucket: "action",
      group: urgent ? "now" : "today",
      ownerId: item.assignedTo,
      coverageIds: urgent ? [item.escalationPhysicianId] : [],
      date,
      arrivedAt: date,
      dateLabel: "Received",
      when: shortDate(date),
      action: "Review fax",
      opens: "fax",
      can: none,
      reportId: null,
      urgent,
    };
  });
  if (connection?.failureCode)
    rows.unshift({
      key: `fax:connection:${connection.id}`,
      kind: "fax",
      id: "",
      version: connection.version,
      patientId: "",
      patientName: "Clinic fax account",
      encounterId: null,
      type: "document",
      title: "Fax connection needs attention",
      detail: referralFaxFailureMessage(connection.failureCode),
      status: "connection_failed",
      pill: "Connection issue",
      tone: "amber",
      bucket: "action",
      group: "today",
      ownerId: connection.faxAdminId,
      coverageIds: [],
      date: connection.lastPolledAt,
      arrivedAt: connection.lastPolledAt,
      dateLabel: "Last checked",
      when: "Needs attention",
      action: "Review fax",
      opens: "fax",
      can: none,
      reportId: null,
      urgent: false,
    });
  return rows;
}
/**
 * Patient texts and emails that need a person (PA-7): one row per booking, kind and channel that
 * failed, or whose contact the patient agreed to cannot be used, for bookings still ahead. The
 * rows have no owner; the front desk and clinic administrators see them as their own. "Review
 * message" opens the booking's texts and emails in the right panel (`?patientMessage=<id>`);
 * "Mark done" acknowledges the message, so it leaves the list. A number that replied STOP and a
 * message that may not have been sent are not rows: the booking's messages show them.
 */
export function patientMessageAttentionRows(
  list: PatientMessageAttentionList | undefined,
  now = Date.now(),
): InboxRow[] {
  if (list === undefined) return [];
  const visit = (instant: string) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: list.timeZone,
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(instant));
  return list.items.map((item) => {
    const problem = patientMessageProblemLabel(item.status, item.channel);
    return {
      key: `patient_message:${item.messageId}`,
      kind: "patient_message",
      id: item.messageId,
      version: null,
      patientId: item.patientId ?? "",
      patientName: item.patientName,
      encounterId: null,
      type: "patient_message",
      title: `${problem.label} · ${PATIENT_MESSAGE_KIND_LABELS[item.kind]}`,
      detail: [
        `Visit ${visit(item.startAt)}`,
        ...(item.recipientMasked === null ? [] : [item.recipientMasked]),
        patientMessageReasonText(item.reason, item.channel, item.kind),
      ].join(" · "),
      status: item.status,
      pill: item.status === "withheld" ? "Not sent" : "Not delivered",
      tone: "amber",
      bucket: "action",
      group: groupFor("action", "amber", false, item.updatedAt, now),
      ownerId: null,
      coverageIds: [],
      date: item.updatedAt,
      arrivedAt: item.updatedAt,
      dueAt: item.startAt,
      dateLabel: "Received",
      when: whenLabel(item.updatedAt, "Received", "action", now),
      action: "Review message",
      opens: "patient_message",
      can: { assign: null, snooze: null, done: "acknowledge" },
      reportId: null,
      urgent: false,
      appointmentId: item.appointmentId,
    };
  });
}
export function conversationRows(items: ClinicConversation[], userId: string): InboxRow[] {
  return [...new Map([...items].reverse().map((c) => [c.id, c])).values()].map((c) => ({
    key: `message:${c.id}`,
    kind: "message",
    id: c.id,
    version: c.version,
    patientId: c.patientId ?? "",
    patientName: c.patientName ?? "Clinic message",
    encounterId: c.request?.encounterId ?? null,
    type: "message",
    title: c.subject,
    detail: c.preview,
    status: c.bucket,
    pill: c.unread ? "Unread" : null,
    tone: c.unread ? "blue" : "neutral",
    bucket: c.bucket,
    group: groupFor(c.bucket, "neutral", false, c.updatedAt, Date.now()),
    ownerId: userId,
    coverageIds: [],
    date: c.bucket === "completed" ? (c.completedAt ?? null) : c.updatedAt,
    arrivedAt: c.updatedAt,
    dueAt: c.request?.dueAt ?? null,
    completedAt: c.bucket === "completed" ? (c.completedAt ?? null) : null,
    unread: c.unread,
    dateLabel: c.bucket === "completed" ? "Completed" : "Received",
    when:
      c.bucket === "completed"
        ? c.completedAt
          ? shortDate(c.completedAt)
          : ""
        : shortDate(c.updatedAt),
    action: "Open",
    opens: "message",
    can: none,
    reportId: null,
    urgent: false,
  }));
}
/** Everyone besides the owner who sees a row as their own: no repeats, never the owner. */
const coverage = (ownerId: string | null, ids: readonly (string | undefined)[]) => [
  ...new Set(ids.filter((id): id is string => id !== undefined && id !== ownerId)),
];

const DAY = 86_400_000;
const closed = (status: string) =>
  ["closed", "cancelled", "cannot_complete", "completed"].includes(status);
const due = (date: string | null, now: number) => date !== null && Date.parse(date) <= now;
/** Work cannot enter Needs action before it exists, even if its due date is backdated. */
const laterDate = (...values: (string | null | undefined)[]): string | null =>
  values.reduce<string | null>((latest, value) => {
    if (!value || !Number.isFinite(Date.parse(value))) return latest;
    return !latest || Date.parse(value) > Date.parse(latest) ? value : latest;
  }, null);
const administrative = new Set(["identity_conflict", "awaiting_routing"]);
const arrivalLabels = new Set(["Received", "Uploaded", "Reviewed"]);

const startOfDay = (value: number) => {
  const day = new Date(value);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
};
/** Whole calendar days from today to the date; negative when the date has passed. */
const calendarDays = (date: string, now: number) =>
  Math.round((startOfDay(Date.parse(date)) - startOfDay(now)) / DAY);
export const shortDate = (value: string | null, now = Date.now()) => {
  if (!value || !Number.isFinite(Date.parse(value))) return "—";
  const date = new Date(value);
  return new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === new Date(now).getFullYear() ? {} : { year: "numeric" }),
  }).format(date);
};
const weekday = (value: string) =>
  new Intl.DateTimeFormat("en-CA", { weekday: "short" }).format(new Date(value));

/** The one time fact a row shows. Arrival dates read backwards, due dates read forwards. */
export function whenLabel(
  date: string | null,
  dateLabel: string,
  bucket: InboxBucket,
  now = Date.now(),
): string {
  if (!date || !Number.isFinite(Date.parse(date))) return "";
  if (bucket === "completed") return shortDate(date, now);
  const days = calendarDays(date, now);
  if (arrivalLabels.has(dateLabel))
    return days === 0 ? "Today" : days === -1 ? "Yesterday" : shortDate(date, now);
  if (days < 0) return `${-days}d overdue`;
  if (days === 0) return "Due today";
  if (days === 1) return "Tomorrow";
  if (days <= 6) return weekday(date);
  return shortDate(date, now);
}

function groupFor(
  bucket: InboxBucket,
  tone: InboxTone,
  urgent: boolean,
  date: string | null,
  now: number,
): InboxGroup {
  if (bucket === "completed") {
    const days = date ? calendarDays(date, now) : -2;
    return days === 0 ? "today" : days === -1 ? "yesterday" : "earlier";
  }
  if (urgent || tone === "red") return "now";
  if (!date) return "today";
  const days = calendarDays(date, now);
  return days <= 0 ? "today" : days <= 6 ? "week" : "later";
}

/** Everything the list and the tracking panel agree on about one investigation. */
export function describeObligation(work: Obligation, now = Date.now()) {
  const complete = closed(work.status);
  const taskOpen = work.task !== null && !closed(work.task.state);
  const contactDue = taskOpen && due(work.task?.nextFollowUpAt ?? null, now) && !work.received;
  const overdue =
    !work.received &&
    work.timing.configured &&
    !work.timing.waitingForDependency &&
    due(work.timing.overdueAt, now);
  const needsTiming = !complete && !work.received && !work.timing.configured;
  const needsContact =
    !complete && !work.timing.waitingForDependency && (contactDue || (overdue && !taskOpen));
  return { complete, taskOpen, overdue, needsTiming, needsContact };
}

/**
 * One visible next action per item, only when this person can take it; receiving a report is
 * distinct from closing its investigation. `userId` decides whose follow-ups can be put off.
 */
export function documentInboxRows(
  data: DocumentInbox,
  role: string,
  now = Date.now(),
  userId?: string,
): InboxRow[] {
  const physician = role === "physician";
  const rows: InboxRow[] = [];
  const patientNames = new Map<string, string>();
  for (const entry of [
    ...data.reports.map(({ report, patientName }) => ({
      patientId: report.patientId,
      patientName,
    })),
    ...data.obligations,
  ])
    patientNames.set(entry.patientId, entry.patientName);
  const encounterForReport = (reportId: string) =>
    data.obligations.find((work) => work.receivedReportIds.includes(reportId))?.encounterId ?? null;
  const finish = (row: Omit<InboxRow, "group" | "when">): InboxRow => ({
    ...row,
    // Retain urgency metadata for callers that request priority ordering.
    group: groupFor(row.bucket, row.tone, row.urgent || row.abnormal === true, row.date, now),
    when: whenLabel(row.date, row.dateLabel, row.bucket, now),
  });

  for (const { report, patientName, receivedAt, handoff, abnormal } of data.reports) {
    const complete = ["reviewed", "duplicate", "administrative", "superseded"].includes(
      report.reviewStatus,
    );
    // Once reviewed (patient contact pending or closed), an abnormal report sorts as any other.
    const abnormalUnreviewed =
      abnormal && !complete && report.reviewStatus !== "communication_pending";
    const urgent =
      report.extracted.urgent &&
      !report.acknowledged &&
      !["duplicate", "administrative", "superseded"].includes(report.reviewStatus);
    const status =
      {
        identity_conflict: "Check patient identity",
        awaiting_routing: "Needs a physician",
        awaiting_review: "Needs review",
        communication_pending: report.communicationRecorded
          ? "Ready for closure"
          : "Patient contact needed",
        reviewed: "Reviewed",
        duplicate: "Duplicate",
        administrative: "Filed",
        superseded: "Replaced",
      }[report.reviewStatus] ?? "Needs review";
    const action =
      report.reviewStatus === "identity_conflict"
        ? "Fix identity"
        : report.reviewStatus === "awaiting_routing"
          ? "Assign"
          : urgent
            ? "Acknowledge"
            : report.reviewStatus === "communication_pending"
              ? report.communicationRecorded
                ? physician
                  ? "Sign off"
                  : null
                : "Tell patient"
              : !complete && physician
                ? "Review"
                : null;
    const linked = data.obligations.filter((work) => work.receivedReportIds.includes(report.id));
    rows.push(
      finish({
        key: `report:${report.id}`,
        kind: "report",
        id: report.id,
        version: report.version,
        patientId: report.patientId,
        patientName,
        encounterId: linked[0]?.encounterId ?? null,
        type: reportTypes[report.extracted.category] ?? "document",
        title: report.extracted.title,
        detail: report.extracted.author ?? "Uploaded report",
        status: urgent ? "Urgent · acknowledge" : status,
        pill: urgent
          ? "Urgent"
          : report.reviewStatus === "identity_conflict"
            ? "Identity check"
            : abnormal
              ? "Abnormal"
              : null,
        tone: urgent
          ? "red"
          : complete
            ? "green"
            : administrative.has(report.reviewStatus) || abnormal
              ? "amber"
              : "blue",
        bucket: complete && !urgent ? "completed" : "action",
        ownerId: report.ownerId,
        // The backup owners of the investigations it answers, and whoever holds a request about it.
        coverageIds: coverage(report.ownerId, [
          ...(report.provenance.copyPhysicianIds ?? []),
          ...linked.map((work) => work.backupOwnerId ?? undefined),
          handoff?.assignedTo,
        ]),
        date: complete && report.reviewedAt ? report.reviewedAt : receivedAt,
        arrivedAt: receivedAt,
        completedAt: complete ? report.reviewedAt : null,
        dateLabel: complete && report.reviewedAt ? "Reviewed" : "Received",
        action,
        opens: "report",
        // Any role may hand a live report to a site physician; nothing else closes it from a list.
        can: {
          assign: report.reviewStatus === "superseded" || complete ? null : "physician",
          snooze: null,
          done: null,
        },
        reportId: report.id,
        urgent,
        abnormal: abnormalUnreviewed,
      }),
    );
  }

  for (const work of data.obligations) {
    const { complete, taskOpen, overdue, needsTiming, needsContact } = describeObligation(
      work,
      now,
    );
    const status = complete
      ? work.status === "closed"
        ? "Completed"
        : "Discontinued"
      : work.received
        ? "Received · review pending"
        : needsTiming
          ? "Set a due date"
          : work.timing.waitingForDependency
            ? "Waiting for prerequisite"
            : needsContact
              ? work.partiallyReceived
                ? "Partial · overdue"
                : "Overdue"
              : taskOpen
                ? "Contact scheduled"
                : work.partiallyReceived
                  ? "Partially received"
                  : "Awaiting results";
    const contactOwner = work.task?.ownerId ?? work.timing.staffOwnerId;
    const reviewable = work.received && work.receivedReportIds.length > 0 && !complete;
    rows.push(
      finish({
        key: `obligation:${work.id}`,
        kind: "obligation",
        id: work.id,
        version: work.version,
        patientId: work.patientId,
        patientName: work.patientName,
        encounterId: work.encounterId,
        type: investigationType(work.eventType),
        title: work.title || work.requestedTests.join(", ") || investigationLabel(work.eventType),
        detail: work.source ?? investigationLabel(work.eventType),
        status,
        pill: complete
          ? work.status === "closed"
            ? null
            : "Discontinued"
          : work.received
            ? "Result in"
            : needsTiming
              ? "Untracked"
              : work.timing.waitingForDependency
                ? "After prerequisite"
                : work.partiallyReceived
                  ? "Partial"
                  : null,
        tone: complete
          ? "green"
          : needsContact
            ? "red"
            : needsTiming || work.partiallyReceived
              ? "amber"
              : work.received
                ? "blue"
                : "neutral",
        bucket: complete ? "completed" : needsTiming || needsContact ? "action" : "waiting",
        ownerId:
          needsTiming || work.received
            ? work.ownerId
            : taskOpen || needsContact
              ? (contactOwner ?? work.ownerId)
              : work.ownerId,
        coverageIds: work.backupOwnerId ? [work.backupOwnerId] : [],
        arrivedAt: needsContact
          ? laterDate(
              work.createdAt,
              taskOpen ? work.task?.createdAt : null,
              taskOpen ? work.task?.nextFollowUpAt : work.timing.overdueAt,
            )
          : (work.createdAt ?? null),
        completedAt: work.completedAt ?? null,
        dueAt:
          complete || work.received
            ? null
            : taskOpen
              ? (work.task?.nextFollowUpAt ?? null)
              : work.timing.expectedBy,
        date: complete
          ? (work.completedAt ?? null)
          : work.received
            ? null
            : taskOpen
              ? (work.task?.nextFollowUpAt ?? null)
              : work.timing.expectedBy,
        dateLabel: complete ? "Completed" : taskOpen ? "Next contact" : "Expected",
        action: complete
          ? null
          : needsTiming
            ? physician
              ? "Set due date"
              : null
            : needsContact && taskOpen
              ? "Call patient"
              : needsContact && physician
                ? "Change dates"
                : reviewable && physician
                  ? "Review"
                  : null,
        opens: reviewable ? "report" : "tracking",
        // Only a physician moves an investigation's dates or stops it; staff put it off by
        // recording a contact attempt, which needs the chase task the worker opens when overdue.
        can: complete
          ? none
          : {
              assign: physician && work.timing.expectedBy !== null ? "staff" : null,
              snooze: physician ? "dates" : taskOpen ? "contact" : null,
              done: physician ? "discontinue" : null,
            },
        reportId: work.receivedReportIds[0] ?? null,
        urgent: needsContact && work.timing.urgency !== "routine" && overdue,
      }),
    );
  }

  for (const task of data.followUps) {
    const complete = closed(task.status);
    const isDue = due(task.dueAt, now);
    const ownerId = task.ownerId ?? task.ownerUserId ?? null;
    rows.push(
      finish({
        key: `follow_up:${task.id}`,
        kind: "follow_up",
        id: task.id,
        version: task.reportVersion ?? null,
        patientId: task.patientId,
        patientName: task.patientName ?? patientNames.get(task.patientId) ?? "Patient follow-up",
        encounterId: encounterForReport(task.reportId),
        type: "follow_up",
        title: task.summary,
        detail: "Follow-up task",
        status: complete
          ? task.status === "cancelled"
            ? "Cancelled"
            : "Completed"
          : isDue
            ? "Follow-up due"
            : "Scheduled",
        pill: complete && task.status === "cancelled" ? "Cancelled" : null,
        tone: complete ? "green" : isDue ? "amber" : "neutral",
        bucket: complete ? "completed" : isDue ? "action" : "waiting",
        ownerId,
        coverageIds: [],
        arrivedAt:
          !complete && isDue ? laterDate(task.createdAt, task.dueAt) : (task.createdAt ?? null),
        dueAt: complete ? null : task.dueAt,
        completedAt: task.completedAt ?? null,
        date: complete ? (task.completedAt ?? null) : task.dueAt,
        dateLabel: complete ? "Completed" : "Due",
        action: complete ? null : physician ? "Mark done" : null,
        opens: "report",
        // A follow-up is closed by a physician and put off by its owner or a physician.
        can: complete
          ? none
          : {
              assign: null,
              snooze:
                physician || (userId !== undefined && ownerId === userId) ? "follow_up" : null,
              done: physician ? "complete" : null,
            },
        reportId: task.reportId,
        urgent: false,
      }),
    );
  }

  for (const original of data.processing) {
    const failed = original.retryAvailable;
    const reconciling = original.status === "complete";
    rows.push(
      finish({
        key: `processing:${original.id}`,
        kind: "processing",
        id: original.id,
        version: original.version,
        patientId: original.patientId,
        patientName:
          original.patientName ?? patientNames.get(original.patientId) ?? "Patient document",
        encounterId: original.encounterId,
        type: "upload",
        title: original.fileName,
        detail:
          documentProcessingFailureMessage(original.failureCode) ??
          (original.pageCount
            ? `${original.completedPages} / ${original.pageCount} pages`
            : "Uploaded document"),
        status: failed
          ? "Processing stopped"
          : original.failureCode
            ? "Retrying automatically"
            : reconciling
              ? "Matching to chart"
              : original.status === "reserved"
                ? "Upload incomplete"
                : "Processing",
        pill: failed
          ? "Stopped"
          : original.status === "reserved"
            ? "Incomplete"
            : original.failureCode
              ? "Retrying"
              : reconciling
                ? "Matching"
                : "Processing",
        tone: failed ? "amber" : "neutral",
        bucket: failed || original.status === "reserved" ? "action" : "waiting",
        ownerId: null,
        coverageIds: [],
        date: original.receivedAt,
        arrivedAt: original.receivedAt,
        dateLabel: "Uploaded",
        action: failed ? "Retry" : null,
        opens: failed ? "retry" : "document",
        can: none,
        reportId: null,
        urgent: false,
      }),
    );
  }
  return sortInboxRows(rows);
}

/**
 * Urgent first, then anything red, then abnormal reports awaiting review, then by date; the key
 * keeps the order stable.
 */
export function sortInboxRows(rows: InboxRow[]): InboxRow[] {
  return rows.sort(
    (a, b) =>
      Number(b.urgent) - Number(a.urgent) ||
      Number(b.tone === "red") - Number(a.tone === "red") ||
      Number(b.abnormal === true) - Number(a.abnormal === true) ||
      (a.date ? Date.parse(a.date) : Infinity) - (b.date ? Date.parse(b.date) : Infinity) ||
      a.key.localeCompare(b.key),
  );
}

export const resultStatusLabel: Record<OrderResultWorkflowStatus, string> = {
  received: "Received",
  unmatched: "Reconciliation required",
  matched: "Matched",
  assigned_for_review: "Clinician review required",
  reviewed: "Reviewed",
  patient_contact_pending: "Patient communication required",
  closure_pending: "Ready for closure",
  closed: "Closed",
};
/** An expected result nothing has arrived for; the queue calls it "unmatched". */
export const missingResultLabel = "Not received yet";
export const referralStatusLabel: Record<ReferralWorkflowStatus, string> = {
  locally_accepted: "Locally accepted",
  package_ready: "Package ready",
  human_selection_required: "Awaiting destination",
  stopped: "Preparation stopped",
  reroute_required: "Reroute required",
  queued: "Queued for connector",
  delivered: "Sent",
  destination_accepted: "Destination accepted",
  destination_rejected: "Destination rejected",
  appointment_scheduled: "Appointment scheduled",
  consult_report_received: "Consult report received",
  consult_report_reviewed: "Consult report reviewed",
  patient_informed: "Patient informed",
  closed: "Closed",
};
const finishRow = (row: Omit<InboxRow, "group" | "when">, now: number): InboxRow => ({
  ...row,
  group: groupFor(row.bucket, row.tone, row.urgent, row.date, now),
  when: whenLabel(row.date, row.dateLabel, row.bucket, now),
});

/** Alert staff is inbox work, even when its source document belongs to a visit. */
export function staffRequestRows(requests: readonly StaffRequest[], now = Date.now()): InboxRow[] {
  return requests
    .filter((request) => request.source === "attention" && !request.referralId)
    .map((request) => {
      const complete = closed(request.status);
      return finishRow(
        {
          key: `staff:${request.id}`,
          kind: "staff",
          id: request.id,
          version: request.version,
          patientId: request.patientId,
          patientName: request.displayName,
          encounterId: request.encounterId ?? null,
          type: "staff",
          title: request.requestText,
          detail: `From ${request.requestedBy}`,
          status: complete
            ? "Done"
            : request.status === "in_progress"
              ? "In progress"
              : "Staff request",
          pill: request.status === "in_progress" ? "In progress" : null,
          tone: complete ? "green" : "blue",
          bucket: complete ? "completed" : "action",
          ownerId: request.assignedTo ?? null,
          coverageIds: [],
          date: complete ? (request.completedAt ?? null) : (request.dueAt ?? null),
          arrivedAt: request.createdAt,
          dueAt: complete ? null : (request.dueAt ?? null),
          completedAt: request.completedAt ?? null,
          dateLabel: complete ? "Completed" : "Due",
          action: complete ? null : "Open request",
          opens: "staff",
          can: none,
          reportId: null,
          urgent: false,
        },
        now,
      );
    });
}

/**
 * The order-result work queue as inbox rows. Only clinicians see this queue; the server turns
 * everyone else away, so the caller passes whatever it managed to load.
 */
export function resultQueueRows(
  items: readonly OrderResultWorkQueueItem[],
  role: string,
  now = Date.now(),
): InboxRow[] {
  const physician = role === "physician";
  return sortInboxRows(
    items.map((item) => {
      const closed = item.status === "closed";
      const open = item.exceptions.filter((exception) => exception.state === "open");
      const critical = item.interpretation === "critical" && !closed;
      const unmatched = item.status === "unmatched";
      // An expected result that never arrived: there is nothing to match yet, only to chase.
      const missing = item.resultId === undefined;
      const needsSomeone =
        unmatched ||
        item.status === "assigned_for_review" ||
        item.status === "patient_contact_pending" ||
        item.status === "closure_pending" ||
        open.length > 0 ||
        critical;
      // A missing result's detail ("Not received yet") and due fact already say it all.
      const pill = critical
        ? "Critical"
        : missing
          ? null
          : open.length > 0
            ? `${open.length} exception${open.length === 1 ? "" : "s"}`
            : item.interpretation === "abnormal"
              ? "Abnormal"
              : unmatched
                ? "Unmatched"
                : null;
      const expecting = unmatched && (item.expectedBy ?? item.overdueAt) !== undefined;
      return finishRow(
        {
          key: `result:${item.workItemId}`,
          kind: "result",
          id: item.workItemId,
          version: item.version,
          patientId: item.patientId ?? "",
          patientName: item.patientName ?? item.displayName,
          encounterId: item.encounterId ?? null,
          type: item.resultKind === "imaging_report" ? "imaging" : "lab",
          title: item.resultKind === "imaging_report" ? "Imaging report" : "Laboratory result",
          detail: missing ? missingResultLabel : resultStatusLabel[item.status],
          status: missing ? missingResultLabel : resultStatusLabel[item.status],
          pill,
          tone: closed
            ? "green"
            : critical || open.length > 0
              ? "red"
              : item.interpretation === "abnormal" || unmatched
                ? "amber"
                : "blue",
          bucket: closed ? "completed" : needsSomeone ? "action" : "waiting",
          // The reviewing clinician, or whoever owns the missing-result exception; the backup
          // and whoever holds a staff request about the result cover it.
          ownerId: item.ownerId ?? null,
          coverageIds: coverage(item.ownerId ?? null, [
            item.backupOwnerId,
            item.handoff?.assignedTo,
          ]),
          date: expecting ? (item.expectedBy ?? item.overdueAt ?? null) : item.latestUpdateAt,
          arrivedAt: missing
            ? laterDate(
                open.find((exception) => ["missing_result", "overdue"].includes(exception.kind))
                  ?.openedAt ?? item.latestUpdateAt,
                // A rescheduled exception re-enters Needs action at its new overdue threshold.
                item.overdueAt && due(item.overdueAt, now) ? item.overdueAt : null,
              )
            : (item.receivedAt ?? item.latestUpdateAt),
          dueAt: expecting ? (item.expectedBy ?? item.overdueAt ?? null) : null,
          completedAt: closed ? item.latestUpdateAt : null,
          dateLabel: expecting ? "Expected" : closed ? "Reviewed" : "Received",
          action: closed
            ? null
            : missing
              ? "Follow up"
              : unmatched
                ? "Match"
                : item.status === "assigned_for_review"
                  ? physician
                    ? "Review"
                    : null
                  : item.status === "patient_contact_pending"
                    ? "Tell patient"
                    : item.status === "closure_pending"
                      ? physician
                        ? "Close"
                        : null
                      : critical
                        ? "Review"
                        : null,
          opens: "result",
          // A physician stops tracking a result that will never come back (the investigation is
          // discontinued, which resolves its missing-result exception) and closes a received
          // result that is ready for closure; a received result is never closed unreviewed.
          can: {
            assign: null,
            snooze: null,
            done:
              !physician || closed
                ? null
                : item.resultId === undefined
                  ? item.expectedEventId === undefined
                    ? null
                    : "stop_tracking"
                  : item.status === "closure_pending"
                    ? "close_result"
                    : null,
          },
          reportId: item.resultId ?? null,
          urgent: critical,
          ...(item.expectedEventId === undefined ? {} : { expectedEventId: item.expectedEventId }),
        },
        now,
      );
    }),
  );
}

/** The referral work queue as inbox rows: the patient leads, the destination is the item. */
export function referralQueueRows(
  items: readonly ReferralWorkQueueItem[],
  role: string,
  now = Date.now(),
  userId?: string,
): InboxRow[] {
  const physician = role === "physician";
  return sortInboxRows(
    items.map((item) => {
      const closed = item.status === "closed";
      const open = item.exceptions.filter((exception) => exception.state === "open");
      const stuck =
        item.status === "human_selection_required" ||
        item.status === "destination_rejected" ||
        item.status === "stopped" ||
        item.status === "reroute_required";
      const toSend =
        item.status === "package_ready" && (item.channel === "fax" || item.handoff !== undefined);
      // The clinician can follow a delegated package in Queued while its holder sends it.
      // Unassigned and self-assigned packages still need the clinician's attention.
      const delegatedSend =
        toSend &&
        physician &&
        userId !== undefined &&
        item.handoff?.assignedTo !== undefined &&
        item.handoff.assignedTo !== userId;
      const pill =
        open.length > 0
          ? open[0]?.kind === "connector_failed"
            ? "Connector failed"
            : open[0]?.kind === "overdue"
              ? "Overdue"
              : "Escalated"
          : item.status === "destination_rejected"
            ? "Declined"
            : item.status === "human_selection_required"
              ? "Needs destination"
              : item.status === "stopped"
                ? "Stopped"
                : item.status === "reroute_required"
                  ? "Reroute"
                  : toSend
                    ? "To send"
                    : null;
      const needsSomeone =
        stuck ||
        open.length > 0 ||
        (toSend && !delegatedSend) ||
        item.status === "consult_report_received" ||
        item.status === "consult_report_reviewed";
      return finishRow(
        {
          key: `referral:${item.referralId}`,
          kind: "referral",
          id: item.referralId,
          version: item.version,
          patientId: item.patientId ?? "",
          patientName: item.patientName ?? "Unknown patient",
          encounterId: item.encounterId ?? null,
          type: "referral",
          title: item.destinationDisplayName,
          detail: referralStatusLabel[item.status],
          status: referralStatusLabel[item.status],
          pill,
          tone: closed
            ? "green"
            : open.length > 0 || item.status === "destination_rejected"
              ? "red"
              : stuck
                ? "amber"
                : item.status === "consult_report_received" ||
                    item.status === "consult_report_reviewed"
                  ? "blue"
                  : "neutral",
          bucket: closed ? "completed" : needsSomeone ? "action" : "waiting",
          // Whoever holds the print-and-send hand-off owns the row (REF-10); otherwise the
          // referring clinician does, and still sees a held row as their own.
          ownerId: item.handoff?.assignedTo ?? item.ownerId ?? null,
          coverageIds: coverage(item.handoff?.assignedTo ?? item.ownerId ?? null, [
            item.ownerId,
            item.backupOwnerId,
          ]),
          date: closed ? item.latestUpdateAt : (item.nextExpectedEvent?.expectedBy ?? null),
          arrivedAt:
            open
              .map((exception) => exception.openedAt)
              .sort((a, b) => Date.parse(a) - Date.parse(b))[0] ?? item.latestUpdateAt,
          dueAt: closed ? null : (item.nextExpectedEvent?.expectedBy ?? null),
          completedAt: closed ? item.latestUpdateAt : null,
          dateLabel: closed ? "Reviewed" : "Expected",
          action: closed
            ? null
            : toSend
              ? delegatedSend
                ? null
                : "Send"
              : item.status === "human_selection_required" ||
                  item.status === "destination_rejected" ||
                  item.status === "reroute_required"
                ? "Pick a clinic"
                : item.status === "consult_report_received"
                  ? physician
                    ? "Sign off"
                    : null
                  : item.status === "consult_report_reviewed"
                    ? "Tell patient"
                    : null,
          opens: "referral",
          can: none,
          reportId: null,
          urgent: false,
        },
        now,
      );
    }),
  );
}
